import {
  ApiPermissionsSchemaType,
  EmployeePermissionSchemaType,
} from "@/pages/settings/roles/edit/role-edit.types.ts";
import { ApiField } from "@/types/custom-field.types.ts";
import { ApiFolder } from "@/types/document.types.ts";
import {
  ApiPermission,
  EmployeeFieldPermission,
  PermissionType,
  PrincipalType,
  RoleEditTab,
  RoleType,
} from "@/types/role.types.ts";
import { ruleGroupsSchema } from "@/components/apply-to/rule-groups.schema.ts";
import { ApiWorkflowShort } from "@/types/workflows/workflow.types.ts";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export const useRoleEditSchema = (
  customFields?: ApiField[],
  folders?: ApiFolder[],
  workflows?: ApiWorkflowShort[]
) => {
  const { t } = useTranslation();

  return useMemo(() => {
    const permissionSchema = () => z.enum(PermissionType);
    const customFieldsSchema = Object.fromEntries(
      (customFields ?? []).map((field) => [field.slug, permissionSchema()])
    );
    const foldersSchema = Object.fromEntries(
      (folders ?? []).map((folder) => [folder.id, permissionSchema()])
    );
    const workflowPermissionSchema = () =>
      z.object({ run: permissionSchema(), executions: permissionSchema() });
    const workflowsSchema = Object.fromEntries(
      (workflows ?? []).map((workflow) => [workflow.id, workflowPermissionSchema()])
    );

    return z.object({
      title: z.string({ error: t("errors:field_required") }).min(1, t("errors:field_required")),
      description: z.string().default(""),
      type: z.enum(RoleType),
      principal_type: z.enum(PrincipalType).default(PrincipalType.employee),
      api_user_entities: z
        .array(
          z.object({
            id: z.union([z.number(), z.string()]),
            title: z.string().min(1),
          })
        )
        .default([]),
      enable_apply_to: z.boolean().default(false),
      api_permissions: z.object(
        Object.keys(ApiPermission).reduce(
          (acc, key) => ({
            ...acc,
            [key]: z.enum(PermissionType).optional(),
          }),
          {}
        ) as ApiPermissionsSchemaType
      ),
      employee_field_permissions: z.object(
        Object.keys(EmployeeFieldPermission).reduce(
          (acc, key) => ({ ...acc, [key]: permissionSchema() }),
          {}
        ) as EmployeePermissionSchemaType
      ),
      employee_custom_field_permissions: z.object(customFieldsSchema).catchall(permissionSchema()),
      employee_custom_table_field_permissions: z
        .record(z.string(), z.unknown())
        .default({}) as unknown as z.ZodType<
        Record<
          number,
          {
            manage_table: PermissionType;
            history: PermissionType;
            effective_from: PermissionType;
            changed_by: PermissionType;
            fields_permissions: Record<string, PermissionType>;
          }
        >
      >,
      employee_folder_permissions: z.object(foldersSchema).catchall(permissionSchema()),
      employee_workflow_permissions: z.object(workflowsSchema).catchall(workflowPermissionSchema()),
      role_tab: z.string().default(RoleEditTab.data),
      assign_to: ruleGroupsSchema(),
      apply_to: ruleGroupsSchema(),
    });
  }, [customFields, folders, t, workflows]);
};

export type EditRoleSchemaInput = z.input<ReturnType<typeof useRoleEditSchema>>;
export type EditRoleSchema = z.output<ReturnType<typeof useRoleEditSchema>>;
